
import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { type StateNodeData } from '@/types/workflow-graph';
import { cn } from '@/lib/utils';

interface FanInNodeProps {
    data: StateNodeData & { branchCount?: number };
    selected?: boolean;
}

/**
 * Fan-in node represents the end of parallel execution.
 * Displayed as a vertical or horizontal bar where multiple branches join.
 * Orientation changes based on layoutDirection:
 * - LR (horizontal flow): Vertical bar with handles left/right
 * - TB (vertical flow): Horizontal bar with handles top/bottom
 */
export const FanInNode = memo(function FanInNode({ data, selected }: FanInNodeProps) {
    const branchCount = data.branchCount || 2;
    const { layoutDirection = 'LR' } = data;
    
    // Determine if we're in horizontal (LR) or vertical (TB) flow
    const isHorizontal = layoutDirection === 'LR';
    
    // Bar size based on branches
    const barSize = Math.max(80, branchCount * 40);
    
    // Container dimensions based on orientation
    const containerStyle = isHorizontal 
        ? { width: 16, height: barSize }
        : { width: barSize, height: 16 };
    
    // Bar styling based on orientation
    const barClassName = isHorizontal
        ? 'absolute inset-0 w-2 mx-auto rounded-full bg-gradient-to-b from-gray-900 to-gray-700'
        : 'absolute inset-0 h-2 my-auto rounded-full bg-gradient-to-r from-gray-900 to-gray-700';
    
    // Input handle position
    const inputPosition = isHorizontal ? Position.Left : Position.Top;
    
    // Output handle position
    const outputPosition = isHorizontal ? Position.Right : Position.Bottom;
    const outputStyle = isHorizontal 
        ? { top: '50%', transform: 'translateY(-50%)' }
        : { left: '50%', transform: 'translateX(-50%)' };

    return (
        <div 
            className="relative"
            style={containerStyle}
        >
            {/* Input Handles - distributed along the bar */}
            {Array.from({ length: branchCount }).map((_, idx) => {
                const spacing = barSize / (branchCount + 1);
                const position = spacing * (branchCount - idx);
                
                const handleStyle = isHorizontal
                    ? { top: position, transform: 'translateY(-50%)' }
                    : { left: position, transform: 'translateX(-50%)' };

                return (
                    <Handle
                        key={`branch-${idx}`}
                        type="target"
                        position={inputPosition}
                        id={`branch-${idx}`}
                        className="!h-3 !w-3 !border-gray-900 !bg-gray-800"
                        style={handleStyle}
                    />
                );
            })}

            {/* Bar - vertical for LR, horizontal for TB */}
            <div
                className={cn(
                    barClassName,
                    'shadow-md',
                    selected && 'ring-2 ring-blue-500 ring-offset-2'
                )}
            />

            {/* Output Handle (right for LR, bottom for TB) */}
            <Handle
                type="source"
                id="source"
                position={outputPosition}
                className="!h-3 !w-3 !border-gray-900 !bg-gray-800"
                style={outputStyle}
            />
        </div>
    );
});
